import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

// Format ROI as multiplier
const formatROI = (value) => `${(value / 100).toFixed(1)}x`;

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-sm text-slate-600">
          ROI: <span className="font-mono font-semibold">{formatROI(payload[0].payload.roi)}</span>
        </p>
      </div>
    );
  }
  return null;
};

export const ROIDistributionChart = ({
  data,
  meanROI,
  p5ROI,
  p95ROI,
  probPositive,
  capped = false,
  title = "ROI Probability Distribution",
}) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center text-slate-500">
            Run a Monte Carlo simulation to see ROI distribution
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform distribution data
  const chartData = data.map((item, index) => ({
    percentile: index * 2,
    roi: item.roi,
    density: Math.exp(-Math.pow((item.roi - (meanROI || 0)) / 50, 2) / 2),
  }));

  return (
    <Card data-testid="roi-distribution-chart">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-lg font-heading">{title}</CardTitle>
            <p className="text-sm text-slate-500">
              Monte Carlo simulation results (5,000 iterations)
            </p>
          </div>
          {Math.abs((p95ROI || 0) - (p5ROI || 0)) <= 10 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-300 bg-teal-100 px-2.5 py-1 text-xs font-semibold text-teal-800" data-testid="roi-chart-confidence-badge">
              <span className="h-2 w-2 rounded-full bg-teal-600" />
              High confidence{capped ? ' · capped at ceiling' : ' · tight band'}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <defs>
                <linearGradient id="roiGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0d9488" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="roi"
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => `${(value / 100).toFixed(0)}x`}
                label={{
                  value: 'ROI (x)',
                  position: 'insideBottom',
                  offset: -5,
                  style: { textAnchor: 'middle', fill: '#64748b', fontSize: 12 },
                }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#64748b' }}
                label={{
                  value: 'Probability Density',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fill: '#64748b', fontSize: 12 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* P5 reference line */}
              {p5ROI !== undefined && (
                <ReferenceLine
                  x={p5ROI}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  label={{
                    value: `P5: ${formatROI(p5ROI)}`,
                    position: 'top',
                    fill: '#ef4444',
                    fontSize: 11,
                  }}
                />
              )}
              
              {/* Mean reference line */}
              {meanROI !== undefined && (
                <ReferenceLine
                  x={meanROI}
                  stroke="#1e3a5f"
                  strokeWidth={2}
                  label={{
                    value: `Mean: ${formatROI(meanROI)}`,
                    position: 'top',
                    fill: '#1e3a5f',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
              )}
              
              {/* P95 reference line */}
              {p95ROI !== undefined && (
                <ReferenceLine
                  x={p95ROI}
                  stroke="#10b981"
                  strokeDasharray="5 5"
                  label={{
                    value: `P95: ${formatROI(p95ROI)}`,
                    position: 'top',
                    fill: '#10b981',
                    fontSize: 11,
                  }}
                />
              )}
              
              {/* Zero line */}
              <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={1} />
              
              <Area
                type="monotone"
                dataKey="density"
                stroke="#0d9488"
                strokeWidth={2}
                fill="url(#roiGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-rose-500" />
            <span className="text-slate-600">5th Percentile (Downside)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-navy-700" />
            <span className="text-slate-600">Mean ROI</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-500" />
            <span className="text-slate-600">95th Percentile (Upside)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-teal-500" />
            <span className="text-slate-600">
              {probPositive !== undefined ? `${probPositive.toFixed(1)}% Positive` : 'Distribution'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ROIDistributionChart;
